import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { notify } from "./notify";

const BODY_TABLE = "community_body";
const CONFIG_TABLE = "committee_config";

/* ── Default permission areas ──────────────────────── */
export const PERMISSION_AREAS = {
  finance: {
    label: "Finance & Accounts",
    description: "Fund accounts, transactions, settlements",
  },
  development: {
    label: "Development",
    description: "Create, classify, and manage development projects",
  },
  vendors: {
    label: "Vendors & Contracts",
    description: "Manage vendors, contracts, and payouts",
  },
  community: {
    label: "Community Setup",
    description: "Blocks, floors, flats, member assignments",
  },
  invitations: {
    label: "Meetings & Invitations",
    description: "Create and manage community meetings",
  },
};

/* ── Suggested committee roles (not mandatory) ───────── */
export const SUGGESTED_ROLES = [
  "President",
  "Vice President",
  "Secretary",
  "Joint Secretary",
  "Treasurer",
];

/* ── Default ownership mapping (suggestive, not restrictive) */
export const DEFAULT_ROLE_AREAS = {
  president: ["finance", "development", "vendors", "community", "invitations"],
  "vice-president": ["development", "vendors", "invitations"],
  "vice president": ["development", "vendors", "invitations"],
  secretary: ["community", "invitations", "development"],
  "joint-secretary": ["community", "invitations"],
  "joint secretary": ["community", "invitations"],
  treasurer: ["finance", "vendors"],
};

/* ── Read all committee assignments (real-time) ────── */
export const getCommitteeInRealTime = (communityCode, cb) => {
  return onSnapshot(
    query(
      collection(db, BODY_TABLE),
      where("communityCode", "==", communityCode)
    ),
    (snap) => {
      const members = [];
      snap.forEach((d) => {
        members.push({ id: d.id, ...d.data() });
      });
      cb(members);
    },
    (err) => {
      console.error("Failed to load committee:", err);
      cb([]);
    }
  );
};

/* ── Assign a person to a committee role ───────────── */
export const assignCommitteeRole = async (
  role,
  userId,
  representatorId,
  communityCode,
  displayName,
  areas
) => {
  await setDoc(doc(db, BODY_TABLE, `${communityCode}_${role}`), {
    role,
    userId,
    representatorId,
    communityCode,
    displayName: displayName || "",
    areas: areas || DEFAULT_ROLE_AREAS[role] || [],
    assignedAt: Timestamp.now(),
  });
  // Notify the assigned person
  notify({
    recipientId: userId,
    category: "community",
    title: "Committee Role Assigned",
    body: `You have been assigned the role of ${role}.`,
    data: { role, communityCode },
  }).catch(() => {});
};

/* ── Remove a committee assignment ─────────────────── */
export const removeCommitteeRole = async (communityCode, role) => {
  return deleteDoc(doc(db, BODY_TABLE, `${communityCode}_${role}`));
};

/* ── Update which areas a role is responsible for ──── */
export const updateRoleAreas = async (communityCode, role, areas) => {
  return updateDoc(doc(db, BODY_TABLE, `${communityCode}_${role}`), {
    areas,
  });
};

/* ── Update action-level permissions for a role ────── */
export const updateRolePermissions = async (
  communityCode,
  role,
  allowedActions,
  areas
) => {
  return updateDoc(doc(db, BODY_TABLE, `${communityCode}_${role}`), {
    allowedActions,
    areas,
  });
};

/* ── Custom (volunteer) roles – stored separately ──── */
export const getVolunteerRolesInRealTime = (communityCode, cb) => {
  return onSnapshot(
    query(
      collection(db, "community_volunteer_roles"),
      where("communityCode", "==", communityCode)
    ),
    (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    () => cb([])
  );
};

export const addVolunteerRole = async (name, communityCode) => {
  const docRef = doc(collection(db, "community_volunteer_roles"));
  await setDoc(docRef, { name, communityCode, createdAt: Timestamp.now() });
  return docRef.id;
};

export const removeVolunteerRole = async (roleId) => {
  return deleteDoc(doc(db, "community_volunteer_roles", roleId));
};

/* ═════════════════════════════════════════════════════════════
 *  MIGRATION — Old fixed-role assignments → unified volunteer roles
 *
 *  Old system stored committee body docs as:
 *    ID: "${communityCode}_president"  →  { role: "president", ... }
 *
 *  New system stores all roles in community_volunteer_roles and
 *  body docs are keyed:  "${communityCode}_${volunteerRoleDocId}"
 *
 *  This function detects orphaned old-style assignments and:
 *    1. Creates a volunteer role doc for each orphaned role name
 *    2. Re-writes the community_body doc with the new role key
 *    3. Deletes the old community_body doc
 * ═════════════════════════════════════════════════════════════ */
const OLD_FIXED_ROLE_NAMES = {
  president: "President",
  "vice-president": "Vice President",
  secretary: "Secretary",
  "joint-secretary": "Joint Secretary",
  treasurer: "Treasurer",
};

export const migrateFixedRoles = async (
  communityCode,
  committeeMembers,
  volunteerRoles
) => {
  const volunteerIds = new Set(volunteerRoles.map((v) => v.id));

  // Find committee members whose role key is an old slug (not a current volunteer role ID)
  const orphaned = committeeMembers.filter(
    (m) => !volunteerIds.has(m.role) && m.role in OLD_FIXED_ROLE_NAMES
  );

  if (orphaned.length === 0) return false;

  // Build a lookup: lowercased name → existing volunteer role
  const nameToVolunteer = {};
  volunteerRoles.forEach((v) => {
    nameToVolunteer[v.name.toLowerCase()] = v;
  });

  for (const member of orphaned) {
    const displayName = OLD_FIXED_ROLE_NAMES[member.role] || member.role;

    // Re-use existing volunteer role if name already matches, otherwise create one
    let newRoleId;
    const existing = nameToVolunteer[displayName.toLowerCase()];
    if (existing) {
      newRoleId = existing.id;
    } else {
      newRoleId = await addVolunteerRole(displayName, communityCode);
      // Cache so next iteration won't duplicate
      nameToVolunteer[displayName.toLowerCase()] = { id: newRoleId, name: displayName };
    }

    // Write new body doc with the volunteer role ID as key
    const oldDocId = `${communityCode}_${member.role}`;
    const newDocId = `${communityCode}_${newRoleId}`;

    // eslint-disable-next-line no-unused-vars
    const { id: _id, ...data } = member;
    await setDoc(doc(db, BODY_TABLE, newDocId), {
      ...data,
      role: newRoleId,
    });

    if (oldDocId !== newDocId) {
      await deleteDoc(doc(db, BODY_TABLE, oldDocId));
    }
  }

  return true; // migration ran
};
